import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Požární ochrana - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a technik PO / osoba odborně způsobilá v PO
 * (vyhl. 246/2001 Sb. §16a). Legal refs: zákon 133/1985 Sb. o požární ochraně;
 * vyhl. 246/2001 Sb. §23-§26 (školení o PO - zaměstnanci každé 2 roky).
 * First bilingual profile (cs-CZ + en-US; renderer falls back to cs-CZ).
 * Three chapters (základy hoření | hasicí přístroje | postup při požáru) +
 * a graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload by the API.
 */
export const fireProtectionProfile: TrainingProfile = {
  slug: 'fire-protection',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column (V1 Czech). Chapter titles are LocalizedText.
  title: 'Požární ochrana',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-zaklady-horeni',
        title: t('Základy hoření a požární nebezpečí', 'Fire basics and fire hazards'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['hs-byt-rizika'],
        blocks: [
          {
            id: 'h-horeni',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Aby hořelo, potřebuje oheň tři věci', 'Fire needs three things to burn'),
          },
          {
            id: 'p-horeni-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Hoření vzniká jen tam, kde se současně potká **hořlavá látka**, **kyslík** a **zdroj zapálení** (tzv. požární trojúhelník). Odeber kteroukoli z těchto tří složek a oheň uhasne - na tomto principu fungují všechny hasební zásahy.',
              'Fire only starts where a **flammable material**, **oxygen** and an **ignition source** meet at the same time (the fire triangle). Remove any one of the three and the fire dies - every extinguishing method works on this principle.',
            ),
          },
          {
            id: 'anim-trojuhelnik',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'fireTriangle',
            caption: t(
              'Požární trojúhelník: odeber palivo, kyslík, nebo teplo - a oheň uhasne.',
              'The fire triangle: remove fuel, oxygen or heat - and the fire dies.',
            ),
          },
          {
            id: 'c-horeni-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **zákon 133/1985 Sb. o požární ochraně** a **vyhl. 246/2001 Sb. §23** (školení zaměstnanců o PO se opakuje **nejméně jednou za 2 roky**).',
              'Legal basis: **Act 133/1985 Coll. on fire protection** and **Decree 246/2001 Coll. §23** (employee fire-safety training repeats **at least every 2 years**).',
            ),
          },
          {
            id: 'hs-byt-rizika',
            blockType: 'hotspotScene',
            schemaVersion: 1,
            scene: 'flatFireRisks',
            hotspots: [
              {
                id: 'hs-zasuvka',
                x: 0.29,
                y: 0.78,
                label: t(
                  'Přetížená prodlužovačka - nejčastější příčina požárů v domácnostech',
                  'Overloaded extension lead - the most common cause of household fires',
                ),
              },
              {
                id: 'hs-unik',
                x: 0.88,
                y: 0.59,
                label: t(
                  'Zastavěná úniková cesta - musí zůstat trvale průchodná',
                  'Blocked escape route - must stay permanently passable',
                ),
              },
              {
                id: 'hs-horlavina',
                x: 0.72,
                y: 0.75,
                label: t(
                  'Ředidlo skladované u kotle - hořlaviny patří mimo dosah zdrojů zapálení',
                  'Solvent stored next to the boiler - keep flammables away from ignition sources',
                ),
              },
              {
                id: 'hs-hlasic',
                x: 0.63,
                y: 0.14,
                label: t(
                  'Hlásič kouře - u novostaveb povinný, jinde důrazně doporučený',
                  'Smoke detector - mandatory in new builds, strongly recommended elsewhere',
                ),
              },
            ],
          },
          {
            id: 'k-horeni',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'U zákazníka pracuješ v cizím prostředí - **rozhlédni se po hořlavinách a únikové cestě dřív, než začneš pracovat**.',
              "You work in someone else's home - **scan for flammables and the escape route before you start working**.",
            ),
          },
        ],
      },
      {
        id: 'ch-hasici-pristroje',
        title: t('Hasicí přístroje a jejich použití', 'Fire extinguishers and their use'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-elektrika'],
        blocks: [
          {
            id: 'h-pristroje',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Správný přístroj na správný požár',
              'The right extinguisher for the right fire',
            ),
          },
          {
            id: 'p-pristroje-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Hasicí přístroje se liší náplní: **vodní** (dřevo, papír, textil), **pěnový** (navíc kapaliny jako benzín a oleje), **práškový** (univerzální, vhodný i na elektřinu) a **sněhový CO₂** (elektronika a jemná zařízení - nezanechává zbytky; na pevné látky třídy A jen omezeně, u malých požárů v uzavřeném prostoru).',
              'Extinguishers differ by agent: **water** (wood, paper, textiles), **foam** (also liquids like petrol and oils), **powder** (universal, safe on electrics) and **CO₂** (electronics and delicate equipment - leaves no residue; of only limited use on class A solids, small fires in an enclosed space).',
            ),
          },
          {
            id: 'anim-pristroje',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'extinguisherMatch',
            steps: [
              {
                label: t('Voda na dřevo', 'Water on wood'),
                say: t(
                  'Třída A (pevné látky): voda hasí dobře.',
                  'Class A (solids): water works well.',
                ),
              },
              {
                label: t('Voda na rozvaděč', 'Water on a live panel'),
                say: t(
                  'Nikdy - voda vede proud, hrozí úraz elektrickým proudem.',
                  'Never - water conducts, risk of electric shock.',
                ),
              },
              {
                label: t('CO₂ nebo prášek na rozvaděč', 'CO₂ or powder on a live panel'),
                say: t(
                  'Nejdřív vypni proud, pak sněhový nebo práškový přístroj.',
                  'Switch the power off first, then CO₂ or powder.',
                ),
              },
            ],
            caption: t(
              'Voda na dřevo ano, na elektřinu nikdy - tam patří CO₂ nebo prášek.',
              'Water on wood yes, on electricity never - that is where CO₂ or powder belongs.',
            ),
          },
          {
            id: 'c-pristroje-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Nikdy nehas vodou ani pěnou zařízení pod elektrickým napětím** - hrozí úraz elektrickým proudem. Nejdřív vypni proud, nebo použij práškový či sněhový přístroj.',
              '**Never use water or foam on live electrical equipment** - you risk electric shock. Cut the power first, or use a powder or CO₂ extinguisher.',
            ),
          },
          {
            id: 'acc-pristroje',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t(
                  'Kdy má smysl hasit svépomocí?',
                  'When does it make sense to fight the fire yourself?',
                ),
                a: t(
                  'Jen v zárodku požáru, když tím neohrozíš sebe ani ostatní a máš volnou únikovou cestu za zády. Rozvinutý požár nech hasičům.',
                  'Only in the earliest stage, when you endanger neither yourself nor others and your escape route stays clear behind you. Leave a developed fire to the fire brigade.',
                ),
              },
              {
                q: t('Jak dlouho hasicí přístroj stříká?', 'How long does an extinguisher last?'),
                a: t(
                  'Běžný přenosný přístroj se vyprázdní za 6-15 sekund. Miř na ohnisko plamene a haste přerušovaně.',
                  'A typical portable unit empties in 6-15 seconds. Aim at the base of the flames and discharge in bursts.',
                ),
              },
            ],
          },
          {
            id: 'kc-elektrika',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Hoří rozvodná skříň pod napětím. Který přístroj použiješ?',
              'A live electrical panel is on fire. Which extinguisher do you use?',
            ),
            choices: [
              { id: 'kc-a', label: t('Vodní', 'Water') },
              { id: 'kc-b', label: t('Sněhový (CO₂)', 'CO₂') },
              { id: 'kc-c', label: t('Pěnový', 'Foam') },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'CO₂ (nebo prášek) nevede elektrický proud. Voda a pěna na zařízení pod napětím nepatří.',
              'CO₂ (or powder) does not conduct electricity. Water and foam must never touch live equipment.',
            ),
          },
        ],
      },
      {
        id: 'ch-postup',
        title: t('Postup při požáru a evakuace', 'Fire response and evacuation'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-postup'],
        blocks: [
          {
            id: 'h-postup',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Když začne hořet', 'When a fire breaks out'),
          },
          {
            id: 'ord-postup',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky při zjištění požáru do správného pořadí.',
              'Put the fire-response steps in the correct order.',
            ),
            items: [
              {
                id: 'op-1',
                label: t('Varuj osoby v okolí („HOŘÍ!")', 'Warn people nearby ("FIRE!")'),
              },
              {
                id: 'op-2',
                label: t(
                  'Volej hasiče - tísňová linka 150',
                  'Call the fire brigade - emergency line 150',
                ),
              },
              {
                id: 'op-3',
                label: t(
                  'Haš jen v zárodku a bez rizika',
                  'Extinguish only an incipient fire, without risk',
                ),
              },
              {
                id: 'op-4',
                label: t('Opusť prostor po únikové cestě', 'Leave via the escape route'),
              },
            ],
          },
          {
            id: 'cl-postup',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Znám únikovou cestu z místa, kde pracuji',
                'I know the escape route from where I work',
              ),
              t(
                'Vím, kde je nejbližší hasicí přístroj',
                'I know where the nearest extinguisher is',
              ),
              t('Nepoužívám výtah při evakuaci', 'I never use a lift during evacuation'),
              t(
                'Zavřu dveře hořící místnosti (nezamykám)',
                'I close (not lock) the door of the burning room',
              ),
            ],
          },
          {
            id: 'c-postup-svar',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Po **svařování, broušení nebo pájení** zajisti požární dohled pracoviště - skryté doutnání se může rozhořet i hodiny po skončení práce (viz školení Svařování).',
              'After **welding, grinding or soldering**, keep a fire watch on the workplace - hidden smouldering can flare up hours after the work ends (see the Welding course).',
            ),
          },
          {
            id: 'k-postup',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Pořadí je vždy **lidé → hasiči (150) → majetek**. Vlastní bezpečí má přednost před hašením.',
              'The order is always **people → fire brigade (150) → property**. Your own safety comes before firefighting.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-pristroj-single',
      kind: 'single',
      chapterId: 'ch-hasici-pristroje',
      prompt: t(
        'Který hasicí přístroj je vhodný na elektroniku pod napětím?',
        'Which extinguisher suits live electronics?',
      ),
      choices: [
        { id: 'a', label: t('Vodní', 'Water') },
        { id: 'b', label: t('Pěnový', 'Foam') },
        { id: 'c', label: t('Sněhový (CO₂)', 'CO₂') },
      ],
      correct: 'c',
    },
    {
      id: 'q-voda-tf',
      kind: 'truefalse',
      chapterId: 'ch-hasici-pristroje',
      prompt: t(
        'Zařízení pod elektrickým napětím lze bezpečně hasit vodou.',
        'Live electrical equipment can be safely extinguished with water.',
      ),
      correct: false,
    },
    {
      id: 'q-trojuhelnik-multi',
      kind: 'multi',
      chapterId: 'ch-zaklady-horeni',
      prompt: t(
        'Které složky tvoří požární trojúhelník? (vyber všechny správné)',
        'Which components form the fire triangle? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Hořlavá látka', 'Flammable material') },
        { id: 'b', label: t('Kyslík', 'Oxygen') },
        { id: 'c', label: t('Oxid uhličitý', 'Carbon dioxide') },
        { id: 'd', label: t('Zdroj zapálení', 'Ignition source') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-linka-numeric',
      kind: 'numeric',
      chapterId: 'ch-postup',
      prompt: t(
        'Jaké číslo má tísňová linka hasičů?',
        'What is the fire brigade emergency number?',
      ),
      correct: 150,
      tolerance: 0,
    },
    {
      id: 'q-postup-order',
      kind: 'ordering',
      chapterId: 'ch-postup',
      prompt: t('Seřaď kroky při zjištění požáru.', 'Order the steps when you discover a fire.'),
      choices: [
        { id: 'a', label: t('Varování osob v okolí', 'Warn people nearby') },
        { id: 'b', label: t('Volání na linku 150', 'Call 150') },
        {
          id: 'c',
          label: t('Hašení v zárodku bez rizika', 'Extinguish incipient fire without risk'),
        },
        { id: 'd', label: t('Evakuace únikovou cestou', 'Evacuate via the escape route') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
